import { useMemo, useState } from 'react';
import { TopicsInfo } from 'api/editor/topics';
import { openInNewTab } from 'tools/tools';
import {
    ElementMetadataFieldName,
    ElementMetadataSectionName,
    ElementTopicsDataType,
    Topic,
} from 'components/common/element-data/types';
import { metadataUsage } from 'hooks/useElementMetadata';
import { SearchTopics } from 'api/search';

export const getOwnerIconConfig = (settings: any, elementData: any) => {
    let owners = [
        { business_owner: settings.metadata.fields.business_owner },
        { data_steward: settings.metadata.fields.data_steward },
        { technical_owner: settings.metadata.fields.technical_owner },
    ];

    const dataAlias: any = {
        business_owner: 'businessOwner',
        data_steward: 'dataSteward',
        technical_owner: 'technicalOwner',
    };

    //sort by owner editor settings
    owners.sort((a: any, b: any) => {
        return a[Object.keys(a)[0]].order - b[Object.keys(b)[0]].order;
    });

    let showIcon = false;
    //display by theme settings and data exist
    //add showIcon prop
    owners = owners.map((ownerField: any) => {
        const key = Object.keys(ownerField)[0];
        const dataObj = ownerField[key];
        const dataKey = dataAlias[key];
        const isDisplay = dataObj.display && elementData[dataKey];
        const isDisplayIcon = isDisplay && !showIcon;
        if (isDisplayIcon) showIcon = true;
        return {
            ...ownerField,
            [key]: {
                ...dataObj,
                display: isDisplay,
                showIcon: isDisplayIcon,
            },
        };
    });

    const result: any = {};
    owners.forEach((ownerItem: any) => {
        const key = Object.keys(ownerItem)[0];
        result[key] = ownerItem[key].showIcon;
    });

    return result;
};

export const getFieldElementMetadataSettings = (settings: any, fieldKey: ElementMetadataFieldName) => {
    const fieldSettings = settings?.metadata?.fields?.[fieldKey];
    return {
        fieldMetadataDisplay: fieldSettings?.display,
        fieldMetadataOrder: fieldSettings?.order,
    };
};
export const getSectionElementMetadataSettings = (settings: any, sectionKey: ElementMetadataSectionName) => {
    const sectionSettings = settings?.metadata?.sections?.[sectionKey];
    return {
        sectionMetadataOrder: sectionSettings?.order,
    };
};

export const adjustTopicsDataFormat = (topicsObj: ElementTopicsDataType | TopicsInfo | SearchTopics) => {
    let newTopicsData: any = { ...topicsObj };
    Object.keys(newTopicsData).forEach((topicKey) => {
        if (newTopicsData[topicKey].hasOwnProperty('topic_type_name')) {
            newTopicsData[topicKey] = {
                ...newTopicsData[topicKey],
                displayOrder: newTopicsData[topicKey]['display_order'],
                topicTypeName: newTopicsData[topicKey]['topic_type_name'],
                useIcon: newTopicsData[topicKey]['use_icon'],
                iconType: newTopicsData[topicKey]['icon_type'],
                faIcon: newTopicsData[topicKey]['fa_icon'],
                topics: newTopicsData[topicKey]['topics'].map((topicItem: any) => {
                    const newTopicItem = { ...topicItem };
                    newTopicItem['topicId'] = newTopicItem['topic_id'];
                    delete newTopicItem['topic_id'];
                    return newTopicItem;
                }),
            };
            ['display_order', 'topic_type_name', 'use_icon', 'icon_type', 'fa_icon'].forEach(
                (fieldKey) => delete newTopicsData[topicKey][fieldKey],
            );
        } else {
            return newTopicsData[topicKey];
        }
    });
    return newTopicsData;
};

export function useTopicsLink(topicsList: Array<Topic>) {
    const [showTermsPopup, setShowTermsPopup] = useState(false);
    const [selectedTopicId, setSelectedTopicId] = useState(0);

    const topicIds: number[] = useMemo(() => topicsList.map((topic) => topic.topicId), [topicsList]);

    const handleTopicClick = (isDefault: boolean, topic: Topic) => {
        if (isDefault) {
            openInNewTab(`/search/global?tag_name=${topic.name}`);
            return;
        }
        setSelectedTopicId(topic.topicId);
        setShowTermsPopup(true);
    };

    return { handleTopicClick, showTermsPopup, setShowTermsPopup, selectedTopicId, topicIds };
}

export const prepareMetadataSettings = (
    metadataSettings: any,
    ownersConfiguration: any,
    usage: metadataUsage,
    ignoreOrder?: boolean,
) => {
    let usageKey = '';
    const sectionsData: any = {};
    const fieldsData: any = {};
    let fieldOrderIndex: number = 0;

    switch (usage) {
        case 'info':
            usageKey = 'i';
            break;
        case 'viewer':
            usageKey = 'v';
            break;
        case 'concierge':
            usageKey = 'c';
            break;
        default:
            usageKey = 'i';
            break;
    }

    (metadataSettings?.metadata ?? []).forEach((section: any, index: number) => {
        sectionsData[section.name] = {
            order: ignoreOrder ? undefined : index + 1,
        };
        (section?.fields ?? []).forEach((fieldBlock: any) => {
            (fieldBlock ?? []).forEach((field: any) => {
                fieldsData[field.name] = {
                    order: ignoreOrder ? undefined : ++fieldOrderIndex,
                    display: usage == 'default' ? true : field.display[usageKey] == true,
                };
            });
        });
    });

    if (usage == 'default' && fieldsData['thumbnail']) {
        fieldsData['thumbnail'].display = false;
    }

    //reorder owners by owner editor settings
    if (fieldsData) {
        if (fieldsData['business_owner']) {
            const ownerBlockOrder = fieldsData['business_owner'].order;
            fieldsData['business_owner'].order = ownerBlockOrder + ownersConfiguration.business_owner.displayOrder;
            if (fieldsData['data_steward'])
                fieldsData['data_steward'].order = ownerBlockOrder + ownersConfiguration.data_steward.displayOrder;
            if (fieldsData['technical_owner'])
                fieldsData['technical_owner'].order =
                    ownerBlockOrder + ownersConfiguration.technical_owner.displayOrder;
        }
    }

    return {
        settings: metadataSettings?.settings ?? {},
        sections: sectionsData,
        fields: fieldsData,
    };
};
